/**
 * API COVERAGE — guided-setup progress (server-side draft)
 *   OnboardingController  GET  /api/onboarding/status
 *                         GET  /api/onboarding/steps/{n}
 *                         PUT  /api/onboarding/steps/{n}
 *                         POST /api/onboarding/steps/{n}/complete
 *                         POST /api/onboarding/exit
 *                         POST /api/onboarding/complete
 *                                                            → onboardingProgressApi.*
 */
import { api } from '@/lib/apiClient';
import type { OnboardingStatus } from '@/types/onboardingProgress';

const BASE = '/onboarding';

/**
 * The setup token, when the caller has not signed in yet.
 *
 * <p>
 * Sent as a HEADER rather than in the body or query string. In a URL it would land in browser history,
 * proxy logs and referrer headers; as a header it goes only where it is meant to. Signed-in callers omit
 * it entirely — their JWT already says which workspace this is.
 * </p>
 */
const withToken = (setupToken?: string | null) =>
  setupToken ? { headers: { 'X-Setup-Token': setupToken } } : undefined;

export const onboardingProgressApi = {
  /** Where setup stands. Called on entry, on refresh, and straight after login. */
  status: (setupToken?: string | null) =>
    api.get<OnboardingStatus>(`${BASE}/status`, undefined, withToken(setupToken)),

  /** Autosave. Saves without marking finished — a half-typed answer is still worth keeping. */
  saveStep: (stepNumber: number, data: unknown, setupToken?: string | null) =>
    api.put<OnboardingStatus>(`${BASE}/steps/${stepNumber}`,
      { data: JSON.stringify(data) }, withToken(setupToken)),

  /**
   * Marks a step finished and moves the flow on.
   *
   * `skipped` records that the person waved it past rather than answering. It still counts toward
   * progress — several steps are optional, and treating a skip as unfinished would strand them there.
   */
  completeStep: (stepNumber: number, data: unknown, setupToken?: string | null, skipped = false) =>
    api.post<OnboardingStatus>(`${BASE}/steps/${stepNumber}/complete`,
      { data: JSON.stringify(data), skipped }, withToken(setupToken)),

  /** Leaves setup, keeping everything. Status stays InProgress. */
  exit: (currentStep: number, data: unknown, setupToken?: string | null) =>
    api.post<OnboardingStatus>(`${BASE}/exit`,
      { currentStep, data: JSON.stringify(data) }, withToken(setupToken)),

  /** Closes setup off after step 14. Idempotent. */
  complete: (setupToken?: string | null) =>
    api.post<OnboardingStatus>(`${BASE}/complete`, {}, withToken(setupToken)),
};
